import React, { useEffect, useState } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { getOrderReports } from "../../../helpers/urlConfig/apiFunctions";
import Select from "react-select";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { dateParser } from "../../../helpers/dateParser";
import { documentTitle } from "../../../helpers/documentTitle";
import errorResponseHandler from "../../../helpers/errorResponseHandler";

const OrderReport = () => {
  const [orders, setOrders] = useState([]);

  /** This Is For Filtration */
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [productTypes, setProductTypes] = useState([]);
  const [huids, setHuids] = useState([]); // State for unique HUIDs
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [selectedHuid, setSelectedHuid] = useState(null); // State for selected HUID
  const [fromDate, setFromDate] = useState(""); // State for From Date
  const [toDate, setToDate] = useState(""); // State for To Date

  const [searchOrderId, setSearchOrderId] = useState("");

  documentTitle(" Order Reports");

  const fetchData = async () => {
    try {
      const response = await getOrderReports();

      if (response.statusCode === 200) {
        const data = response.data;
        setOrders(data);
        setFilteredOrders(data);

        // Extract unique customers, product types, and HUIDs
        const uniqueCustomers = [
          ...new Set(data.map((order) => order.customer_name)),
        ];
        const uniqueProductTypes = [
          ...new Set(data.map((order) => order.product_type_name)),
        ];
        const uniqueHuids = [...new Set(data.map((order) => order.huid))]; // Extract unique HUIDs

        setCustomers(uniqueCustomers);
        setProductTypes(uniqueProductTypes);
        setHuids(uniqueHuids); // Set unique HUIDs
      } else {
        errorResponseHandler(response);
      }
    } catch (error) {
      console.error(error.message);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Filter orders based on selected filters
  useEffect(() => {
    let filtered = [...orders];

    if (selectedCustomer) {
      filtered = filtered.filter(
        (order) => order.customer_name === selectedCustomer.value
      );
    }

    if (selectedProductType) {
      filtered = filtered.filter(
        (order) => order.product_type_name === selectedProductType.value
      );
    }

    if (searchOrderId.trim()) {
      filtered = filtered.filter((order) =>
        order.order_id.toString().includes(searchOrderId.trim())
      );
    }

    if (selectedHuid) {
      filtered = filtered.filter((order) => order.huid === selectedHuid.value); // Filter by HUID
    }

    // Filter by date range
    if (fromDate) {
      filtered = filtered.filter(
        (order) => dateParser(order.created_at) >= fromDate
      );
    }

    if (toDate) {
      filtered = filtered.filter(
        (order) => dateParser(order.created_at) <= toDate
      );
    }

    setFilteredOrders(filtered);
  }, [
    searchOrderId,
    selectedCustomer,
    selectedProductType,
    selectedHuid,
    fromDate,
    toDate,
    orders,
  ]);

  const exportToExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(filteredOrders);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");
    XLSX.writeFile(workbook, "Orders.xlsx");
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.text("Orders List", 14, 10);
    doc.autoTable({
      head: [
        [
          "SL.No",
          "Order ID",
          "Customer Name",
          "Product Type Name",
          "Quantity",
          "HUID",
          "Gram",
          "MC",
          "Amount",
          "Created Date",
        ],
      ],
      body: filteredOrders.map((order, index) => [
        index + 1,
        order.order_id,
        order.customer_name,
        order.product_type_name,
        order.quantity,
        order.huid,
        order.gram,
        order.mc,
        order.amount,
        dateParser(order.created_at),
      ]),
    });
    doc.save("Orders.pdf");
  };

  const dateChangerFunc = (date) => {
    const [year, month, day] = date.split("-"); // Correct: array destructuring
    return `${day}-${month}-${year}`;
  };

  const clearDateFilters = () => {
    setFromDate("");
    setToDate("");
  };

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Sales Reports"
          pageTitle="Sales Reports"
          exportToExcel={exportToExcel}
          exportToPDF={exportToPDF}
        />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <div id="customerList">
                  <Row className="g-1 mb-3">
                    <Col md={1}>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Order ID"
                        value={searchOrderId}
                        onChange={(e) => setSearchOrderId(e.target.value)}
                      />
                    </Col>

                    <Col sm={2}>
                      <Select
                        options={customers.map((customer) => ({
                          value: customer,
                          label: customer,
                        }))}
                        value={selectedCustomer}
                        onChange={setSelectedCustomer}
                        isClearable
                        placeholder="Select Customer"
                      />
                    </Col>

                    <Col md={2}>
                      <Select
                        options={productTypes.map((productType) => ({
                          value: productType,
                          label: productType,
                        }))}
                        value={selectedProductType}
                        onChange={setSelectedProductType}
                        isClearable
                        placeholder="Select Product Type"
                      />
                    </Col>

                    <Col md={2}>
                      <Select
                        options={huids.map((huid) => ({
                          value: huid,
                          label: huid,
                        }))}
                        value={selectedHuid}
                        onChange={setSelectedHuid}
                        isClearable
                        placeholder="Select HUID"
                      />
                    </Col>

                    <Col md={2}>
                      <input
                        type="date"
                        className="form-control"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        placeholder="From Date"
                      />
                    </Col>

                    <Col md={2}>
                      <input
                        type="date"
                        className="form-control"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        placeholder="To Date"
                      />
                    </Col>
                    <Col md={1}>
                      <Button
                        color="secondary"
                        //className="btn-sm w-100"
                        onClick={clearDateFilters}
                        disabled={!fromDate && !toDate}
                      >
                        Clear
                      </Button>
                    </Col>
                  </Row>

                  <div className="table-responsive table-card mt-3 mb-1">
                    <table className="table align-middle table-nowrap">
                      <thead className="table-light">
                        <tr>
                          <th>SL.No</th>
                          <th>Order ID</th>
                          <th>Customer Name</th>
                          <th>Product Type Name</th>
                          <th>Quantity</th>
                          <th>HUID</th>
                          <th>Gram</th>
                          <th>MC</th>
                          <th>Amount</th>
                          <th>Created Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredOrders.map((order, index) => (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{order.order_id}</td>
                            <td>{order.customer_name}</td>
                            <td>{order.product_type_name}</td>
                            <td>{order.quantity}</td>
                            <td>{order.huid}</td>
                            <td>{order.gram}</td>
                            <td>{order.mc}</td>
                            <td>{order.amount}</td>
                            <td>
                              {dateChangerFunc(dateParser(order.created_at))}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {filteredOrders.length === 0 && (
                      <div className="text-center mt-4">
                        <p>No orders found.</p>
                      </div>
                    )}
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default OrderReport;
